import { prop, pick } from 'ramda';

import {
    typeXPath,
    typeDataSource,
    typeNumber,
    typeBoolean,
} from '../constants/parameterTypes';
import {
    paramFormat,
    paramHeight,
    paramMaxWidth,
    paramMaxHeight,
    paramWidth,
} from '../constants/parameterNames';

import resizeImageAsync from '../../../utility/imageResize';

function AlterImage(params) {
    const { commonFunctions: cf } = params;

    const paramImage = 'image';
    const paramImageXPath = 'imagexpath';
    const paramImageDs = 'imageds';
    const paramMaintainProportions = 'maintainproportions';
    const paramQuality = 'quality';

    const description = 'Resizes and/or adjusts the quality of a base64 image.';
    const optionalParameters = [
        {
            name: paramImage,
            description:
                'The base64 value for the source image. Altered image is returned as the qRules Result value.',
        },
        {
            name: paramImageXPath,
            description:
                'XPath to one or more images to modify. Image fields will be modified in-place and the number of images modified will be returned as the qRules Result.',
            type: typeXPath,
            dsParentName: paramImageDs,
        },
        {
            name: paramImageDs,
            description: `Data source for /${paramImageXPath}.If unspecified, the main data source will be used.`,
            type: typeDataSource,
        },
        {
            name: paramWidth,
            description: `The width for the resulting image. If /${paramWidth} is specified and /${paramHeight} is not, the resulting height will either be scaled proportional to the specified width if /${paramMaintainProportions} is true, or will maintain the height of the original image otherwise.`,
            type: typeNumber,
        },
        {
            name: paramMaxWidth,
            description: `Maximum width scale the image to. Ignored if the actual width is already smaller than the specified value`,
            type: typeNumber,
        },
        {
            name: paramHeight,
            description: `The height for the resulting image. If /${paramHeight} is specified and /${paramWidth} is not, the resulting width will either be scaled proportional to the specified height if /${paramMaintainProportions} is true, or will maintain the width of the original image otherwise.`,
            type: typeNumber,
        },
        {
            name: paramMaxHeight,
            description: `Maximum height to scale the image to. Ignored if the actual width is already smaller than the specified value.`,
            type: typeNumber,
        },
        {
            name: paramMaintainProportions,
            description: `(boolean) Maintain the proportions of the original image(s). Defaults to false. Ignored if both /${paramWidth} and /${paramHeight} are specified, or if neither is specified.`,
            type: typeBoolean,
        },
        {
            name: paramQuality,
            description:
                '(number > 0 and <= 100) The quality for the resulting image(s) (only applies to certain image formats, such as jpg).',
            type: typeNumber,
        },
        {
            name: paramFormat,
            description:
                'Format for the resulting image(s), (e.g. png, jpg). Falls back to png if unspecified or if an unsupported value is specified. Subject to browser support, but all browsers should support png and jpg.',
        },
    ];

    // String -> String
    function convertFormat(format) {
        switch (format.toLowerCase()) {
            case 'jpg':
                return 'jpeg';
            case '':
                return 'png';
            default:
                return format.toLowerCase();
        }
    }

    // () -> Parameters
    function getParameters() {
        return {
            image: cf.getParamValue(paramImage),
            imagexpath: cf.getParamValue(paramImageXPath),
            imageds: cf.getParamValue(paramImageDs),
            width: cf.getDoubleParamValue(paramWidth, null, false, true),
            maxWidth: cf.getDoubleParamValue(paramMaxWidth, null, false, true),
            height: cf.getDoubleParamValue(paramHeight, null, false, true),
            maxHeight: cf.getDoubleParamValue(
                paramMaxHeight,
                null,
                false,
                true,
            ),
            maintainProportions: cf.getBoolParamValue(paramMaintainProportions),
            quality:
                cf.getDoubleParamValue(paramQuality, 100, false, true) / 100,
            format:
                'image/' + convertFormat(cf.getParamValue(paramFormat, 'png')),
        };
    }

    // Parameters -> void
    function validateParameters({
        image,
        imagexpath,
        imageds,
        quality,
        width,
        maxWidth,
        height,
        maxHeight,
        maintainProportions,
    }) {
        if (image && (imagexpath || imageds)) {
            throw new Error(
                `/${paramImage} cannot be used at the same time as /${paramImageXPath} or /${paramImageDs}`,
            );
        }
        if (!(image || imagexpath)) {
            throw new Error(
                `Either /${paramImage} or /${paramImageXPath} must be specified.`,
            );
        }
        if (quality <= 0 || quality > 1) {
            throw new Error(
                `/${paramQuality} must be greater than 0 and less than or equal to 100.`,
            );
        }
        if (
            (height !== null && height <= 0) ||
            (width !== null && width <= 0) ||
            (maxHeight !== null && maxHeight <= 0) ||
            (maxWidth !== null && maxWidth <= 0)
        ) {
            throw new Error(
                `/${paramWidth}, /${paramMaxWidth}, /${paramHeight}, and ${paramMaxHeight}, when specified, must be greater than 0.`,
            );
        }
        if ((width && maxWidth) || (height && maxHeight)) {
            throw new Error(
                `Can't use /${paramWidth} with /${paramMaxWidth} or /${paramHeight} with /${paramMaxHeight}`,
            );
        }
        if (
            maintainProportions &&
            ((width && maxHeight) || (maxWidth && height))
        ) {
            throw new Error(
                `Can't use /${paramWidth} with /${paramMaxHeight} or /${paramHeight} with /${paramMaxWidth} if /${paramMaintainProportions} is true`,
            );
        }
    }

    // { converted: Number, errors: [String] } -> qRulesResult
    function imagesResultToQRulesResult({ converted, errors }) {
        return errors.length
            ? {
                  result: converted,
                  success: false,
                  error: `Encountered errors while processing images: ${errors.join(
                      ', ',
                  )}`,
              }
            : { result: converted, success: true };
    }

    // (ResizeImageOptions, String, String) -> { converted: Number, errors: [String] }
    async function convertImageFieldsAsync(options, xpath, dsName) {
        const nodes = cf
            .getValidNodeSet(cf.getDataSource(dsName), xpath)
            .filter((node) => node.value());

        let converted = 0;
        let errors = [];

        for (const node of nodes) {
            try {
                const result = await resizeImageAsync(options, node.value());

                await node.setValueAsync(result);

                converted += 1;
            } catch (err) {
                errors.push(err.message);
            }
        }

        return { converted, errors };
    }

    // Parameters -> qRulesResult
    function processImagesAsync(parameters) {
        const image = prop('image', parameters);
        const convertOptions = pick(
            [
                'width',
                'height',
                'maxWidth',
                'maxHeight',
                'quality',
                'maintainProportions',
                'format',
            ],
            parameters,
        );

        return image
            ? resizeImageAsync(convertOptions, image).then((result) => ({
                  result,
                  success: true,
              }))
            : convertImageFieldsAsync(
                  convertOptions,
                  parameters.imagexpath,
                  parameters.imageds,
              ).then(imagesResultToQRulesResult);
    }

    function executeAsync() {
        const parameters = getParameters();

        validateParameters(parameters);

        return processImagesAsync(parameters);
    }

    return {
        description,
        executeAsync,
        optionalParameters,
    };
}

export default AlterImage;
